import { useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { evaluateCustomCondition, getCurrentContext } from './helpers';
import {
  setResearchAssistantEnabled,
  useAvailabilityMatchers,
  useIsResearchAssistantEnabled,
} from './useResearchAssistantStore';

/**
 * Hook to check if the Research Assistant should be available on the current
 * page. Evaluates the matcher list from the Zustand store against the current
 * route, then mirrors the result back into the store as `isEnabled`.
 */
export const useResearchAssistantAvailability = (): boolean => {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const availabilityMatchers = useAvailabilityMatchers();
  const isEnabled = useIsResearchAssistantEnabled();

  useEffect(() => {
    const shouldBeEnabled = availabilityMatchers.some((matcher) => {
      let pathMatches = false;
      if (matcher.isRegexPath) {
        const regexPattern = new RegExp(matcher.path);
        pathMatches = regexPattern.test(pathname);
      } else {
        pathMatches = pathname === matcher.path;
      }

      if (!pathMatches) return false;

      if (matcher.conditions) {
        if (matcher.conditions.queryParams) {
          const matchesQueryParams = Object.entries(matcher.conditions.queryParams).every(
            ([key, value]) => {
              const paramValue = searchParams.get(key);
              if (value === undefined) return paramValue !== null;
              return paramValue === String(value);
            }
          );

          if (!matchesQueryParams) return false;
        }

        if (matcher.conditions.customCondition) {
          const context = getCurrentContext();
          const customConditionResult = evaluateCustomCondition(
            matcher.conditions.customCondition,
            context
          );

          if (!customConditionResult) {
            return false;
          }
        }
      }

      return true;
    });

    setResearchAssistantEnabled(shouldBeEnabled);
  }, [pathname, searchParams, availabilityMatchers]);

  return isEnabled;
};
